import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';
import { PermissionService } from '../../../core/service/permission/permission.service';

interface Pagination { page: number; limit: number; total: number; totalPages: number; }

export const PAGE_CATEGORIES = [
  { code: 'STATUT_FRMTT', label: 'Statuts FRMTT' },
  { code: 'REGLEMENTS',   label: 'Règlements' },
  { code: 'AG_RAPPORTS',  label: 'Rapports AG' },
  { code: 'LOI_30_09',    label: 'Loi 30-09' },
];

@Component({
  selector: 'app-documents-public',
  standalone: false,
  templateUrl: './documents-public.component.html',
  styleUrls: ['./documents-public.component.scss'],
})
export class DocumentsPublicComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  private api = inject(ApiService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  readonly categories = PAGE_CATEGORIES;

  documents  = signal<any[]>([]);
  isLoading  = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);

  searchQuery      = signal('');
  selectedPageCode = signal('');

  pagination = signal<Pagination>({ page: 1, limit: 15, total: 0, totalPages: 0 });

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  // Form state
  showForm        = signal(false);
  editingDocument = signal<any>(null);
  isSaving        = signal(false);
  formData        = signal({ title: '', order: 1, pageCode: PAGE_CATEGORIES[0].code, fileId: null as number | null });
  selectedFile    = signal<File | null>(null);
  formError       = signal<string | null>(null);

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(q => {
      this.searchQuery.set(q);
      this.load(1, true);
    });

    const docId = this.route.snapshot.paramMap.get('id');
    if (docId && this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path === 'edit') {
      const id = Number(docId);
      this.api.get(`${API.DOCUMENTS.GET_BY_ID}/${id}`).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          const doc = res.data || res;
          this.editingDocument.set(doc);
          this.formData.set({
            title: doc.public_document_first_title || '',
            order: doc.public_document_display_order || 1,
            pageCode: doc.page_code,
            fileId: doc.public_document_file_id || null,
          });
          this.showForm.set(true);
        },
        error: () => this.errorMessage.set('Impossible de charger le document'),
      });
    }

    this.load(1);
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  load(page: number, filtering = false): void {
    filtering ? this.isFiltering.set(true) : this.isLoading.set(true);
    this.errorMessage.set(null);

    this.api.post(API.DOCUMENTS.LIST, {
      page,
      limit: this.pagination().limit,
      search: this.searchQuery() || null,
      page_code: this.selectedPageCode() || null,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.documents.set(res.data || []);
        const total = res.total || 0;
        this.pagination.set({ page, limit: this.pagination().limit, total, totalPages: Math.ceil(total / this.pagination().limit) });
        this.isLoading.set(false);
        this.isFiltering.set(false);
      },
      error: () => {
        this.errorMessage.set('Erreur lors du chargement des documents');
        this.isLoading.set(false);
        this.isFiltering.set(false);
      },
    });
  }

  onSearch(value: string): void { this.searchSubject.next(value); }
  onCategoryFilter(value: string): void { this.selectedPageCode.set(value); this.load(1, true); }
  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.load(page);
  }

  getCategoryLabel(code: string): string {
    return PAGE_CATEGORIES.find(c => c.code === code)?.label ?? code;
  }

  statusLabel(status: string): string {
    return ({ ACTI: 'En ligne', INAC: 'Hors ligne' } as any)[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  onRowClick(id: number): void {
    this.router.navigate(['/federation/documents-publics', id]);
  }

  openAddForm(): void {
    this.editingDocument.set(null);
    this.formData.set({ title: '', order: 1, pageCode: PAGE_CATEGORIES[0].code, fileId: null });
    this.selectedFile.set(null);
    this.formError.set(null);
    this.showForm.set(true);
  }

  closeForm(): void {
    const wasEditing = this.editingDocument();
    this.showForm.set(false);
    this.editingDocument.set(null);
    this.selectedFile.set(null);
    this.formError.set(null);
    if (wasEditing && this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path === 'edit') {
      this.router.navigate(['/federation/documents-publics', wasEditing.public_document_id]);
    }
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile.set(input.files?.[0] ?? null);
  }

  updateField(field: string, value: any): void { this.formData.set({ ...this.formData(), [field]: value }); }

  onSave(): void {
    if (!this.formData().title.trim()) { this.formError.set('Le titre est requis'); return; }
    this.isSaving.set(true);
    this.formError.set(null);

    const file = this.selectedFile();
    const editing = this.editingDocument();
    let body: any;

    if (file) {
      body = new FormData();
      body.append('public_document_first_title', this.formData().title);
      body.append('public_document_display_order', String(this.formData().order));
      body.append('page_code', this.formData().pageCode);
      body.append('file', file);
      if (editing && this.formData().fileId) body.append('public_document_file_id', String(this.formData().fileId));
    } else {
      body = {
        public_document_first_title: this.formData().title,
        public_document_display_order: this.formData().order,
        page_code: this.formData().pageCode,
        public_document_file_id: this.formData().fileId,
      };
    }

    const req = editing
      ? this.api.put(`${API.DOCUMENTS.UPDATE}/${editing.public_document_id}`, body)
      : this.api.post(API.DOCUMENTS.ADD, body);

    req.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSaving.set(false);
        if (editing && this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path === 'edit') {
          this.router.navigate(['/federation/documents-publics', editing.public_document_id]);
        }
        this.closeForm();
        this.load(this.pagination().page);
      },
      error: () => { this.formError.set('Erreur lors de l\'enregistrement'); this.isSaving.set(false); },
    });
  }

}
